import isObject from 'lodash/isObject';
import Cookies from 'js-cookie';
import LCEvent from './customEvents';
import { isEmpty } from './basics';

const webViewBase = '/WebView/';

// askLocation: Afin de demander à l'app native de nous envoyer la dernière localisation
// blobAndroid: Afin de prévenir l'app que l'on veut télécharger un fichier sur Android (via des blob)
// dev: L’utilisateur pourra ouvrir l’hamburger menu et afficher, sur l'environnement de staging, l’option “DEVELOPPEUR”. Cette option permettra à l’application d’afficher une vue en dessous de la webview avec les logs de l’app.
// finishLoading: Afin de gérer l’écran de chargement côté app, la web app pourra envoyé l’information pour masquer l’écran de chargement côté app. (ex: Fin du loading profile)
// log: La webapp peut envoyer ses propres logs afin qu’il soit affichable dans l’app iOS.
// navigatingHomePage: Afin de prévenir l'app que l'utilisateur est revenu sur la home
// refreshToken: La webapp peut envoyer ses propres logs afin qu’il soit affichable dans l’app iOS.
// resetCache: To reset the cache of the app (Deprecated)
// rgpd: Send the rgpd infos to the native app (firebase only)
// modal: Afin que les bandes natives (status bar, home indicator), que la webview ne recouvre pas quand `statusBar.isHidden` vaut false, soient assombries comme le reste de la page quand une modale est ouverte. Payload: { isOpen: boolean }. iOS uniquement, la webview Android est déjà edge-to-edge.
// statusBar: Afin de gérer le clavier et les décalages de la webview, la webapp renverra a tout moment la couleur de la status bar, sous forme d’enum et un boolean pour afficher ou non cette dernière. (format des datas sont en JSON)
// theme: Afin que les zones natives (status bar, navigation bar) suivent le mode clair/sombre choisi dans la webapp. Payload: { mode: 'light' | 'dark' }

export enum WebViewChannel {
    ASK_LOCATION = 'askLocation',
    ASK_SETTING_LOCATION = 'askSettingLocation',
    ASK_SETTING_NOTIFICATION = 'askSettingNotification',
    BLOB_ANDROID = 'blobAndroid',
    DEV = 'dev',
    FINISH_LOADING = 'finishLoading',
    LOG = 'log',
    MODAL = 'modal',
    NAVIGATING_HOME_PAGE = 'navigatingHomePage',
    REFRESH_TOKEN = 'refreshToken',
    RESET_CACHE = 'resetCache',
    RGPD = 'rgpd',
    STATUS_BAR = 'statusBar',
    THEME = 'theme',
}

// ===============================//
// =========== Detection =========//
// ===============================//

export const isDeviceIOS = (): boolean => {
    return typeof window?.navigator !== 'undefined' && /iPad|iPhone|iPod/i.test(window?.navigator?.userAgent);
};

export const isDeviceAndroid = (): boolean => {
    return typeof window?.navigator !== 'undefined' && /android/i.test(window?.navigator?.userAgent);
};

export const isWebView = (): boolean => {
    return window?.navigator?.userAgent?.includes(webViewBase);
};

export const isWebViewIOS = (): boolean => {
    return window?.navigator?.userAgent?.includes(`${webViewBase}iOS`);
};

export const isWebViewAndroid = (): boolean => {
    return window?.navigator?.userAgent?.includes(`${webViewBase}Android`);
};

export const isWebViewMobile = (): boolean => {
    return getMobileOperatingSystem() !== 'UKN' && !isWebView();
};

export const getMobileOperatingSystem = (): string => {
    if (isDeviceAndroid()) {
        return 'Android';
    }

    if (isDeviceIOS()) {
        return 'iOS';
    }

    return 'UKN';
};

// =============================//
// =========== Message =========//
// =============================//

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const sendMessageToNativeApp = (channel: WebViewChannel, message: any): void => {
    if (isWebViewAndroid()) {
        const formattedMessage: string = isObject(message) && !isEmpty(message) ? JSON.stringify(message) : message;
        try {
            if (!window?.[channel]) {
                console.error("ANDROID - Error Channel doesn't exist", channel);
            } else {
                window?.[channel].receiveMessage(formattedMessage);
            }
        } catch (error) {
            console.error('ANDROID - Error invoking channel method', {
                channel,
                formattedMessage,
                error: error.message,
                stack: error.stack,
            });
        }
    } else if (isWebViewIOS()) {
        // A channel added after a native release does not exist in the app installed on the device:
        // reading `.postMessage` on undefined throws and would take down the caller (here, a render
        // effect). Skip instead, the older app simply ignores the feature.
        const handler = window?.webkit?.messageHandlers?.[channel];
        if (!handler) {
            console.error("iOS - Error Channel doesn't exist", channel);
            return;
        }
        handler.postMessage(message);
    }
};

// ===========================//
// =========== Theme =========//
// ===========================//

export type NativeThemeMode = 'light' | 'dark';

// The webapp owns the light/dark preference, but the status bar and the navigation bar are drawn by
// the OS outside of the webview: without this message they keep the appearance they had at launch
// (dark glyphs on a light background) and become unreadable over a dark page.
export const sendThemeModeToNativeApp = (mode: NativeThemeMode): void => {
    if (!isWebView()) {
        return;
    }
    sendMessageToNativeApp(WebViewChannel.THEME, { mode });
};

// ===========================//
// =========== Modal =========//
// ===========================//

// On iOS a page that hands the status bar over to the app (`useWebviewHeader(..., true)`) is drawn
// inside the safe area: the bands above and below it belong to SwiftUI, not to the document, so a
// modal backdrop stops at the edge of the webview and those bands stay bright over a dimmed page.
// The app mirrors the backdrop on them instead. Android needs nothing, its WebView is edge to edge.
export const sendModalStateToNativeApp = (isOpen: boolean): void => {
    if (!isWebViewIOS()) {
        return;
    }
    sendMessageToNativeApp(WebViewChannel.MODAL, { isOpen });
};

// ===========================//
// =========== Token =========//
// ===========================//

// ID returns `cookie.exp` as a serialized Date (ISO string). Numbers are tolerated too, in
// seconds or milliseconds: an epoch read as milliseconds when it was seconds lands in 1970,
// which makes the browser drop the cookie on the spot without any error.
const parseCookieExpiration = (exp: string | number): Date => {
    if (typeof exp === 'number') {
        return new Date(exp < 1e12 ? exp * 1000 : exp);
    }
    return new Date(exp);
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const forgeAndSendCookieToNative = (res: {
    data: { cookie: { exp: number | string; domain?: string }; token: string };
}): boolean => {
    const { token, cookie } = res.data;
    const expirationDate = parseCookieExpiration(cookie?.exp);
    console.log(
        `forging cookie token for WebView : ${token} with expiration ${expirationDate} and domain ${cookie?.domain}`,
    );

    if (!token || Number.isNaN(expirationDate.getTime()) || expirationDate.getTime() <= Date.now()) {
        console.error('forgeAndSendCookieToNative: refusing to forge', { hasToken: !!token, exp: cookie?.exp });
        return false;
    }

    // `secure` must follow the actual protocol: a Secure cookie is dropped over plain http
    // (Safari/WKWebView do not grant localhost an exception), which breaks local dev silently.
    const attributes = {
        expires: expirationDate,
        secure: window.location.protocol === 'https:',
        sameSite: 'lax' as const,
        path: '/',
    };

    Cookies.set('token', token, { ...attributes, domain: cookie?.domain });

    // The browser rejects a domain-scoped cookie whose domain does not match the current host
    // (whitelabel domains, localhost, staging vs prod) and reports nothing. Verify, then retry
    // host-only so the session survives instead of vanishing.
    let stored = document.cookie.includes(token);
    if (!stored) {
        console.log(`Cookie token rejected for domain ${cookie?.domain}, retrying host-only`);
        Cookies.set('token', token, attributes);
        stored = document.cookie.includes(token);
    }
    console.log(stored ? 'Cookie token set successfully' : 'Cookie token could NOT be stored');

    sendMessageToNativeApp(WebViewChannel.REFRESH_TOKEN, {
        token,
        expiredAt: expirationDate,
    });
    console.log('Info sent back to native app successfully');
    window.dispatchEvent(new CustomEvent(LCEvent.TOKEN_RENEWED, { detail: { token_renewed: true } }));

    return stored;
};

// ==============================//
// =========== Firebase =========//
// ==============================//

export const getPlateformTag = (): string => {
    return isWebView() ? (isWebViewIOS() ? 'iOS' : isWebViewAndroid() ? 'Android' : 'web-mobile') : 'web';
};
